/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react'
import * as Flags from 'country-flag-icons/react/3x2'
import { scaleLinear } from 'd3-scale'
import _ from 'lodash'
import { Box } from '@mui/material'
import {
  calculateWeightedGlobalBarChart,
  generateGlobalBarChartData,
  getCountryLongCodeFromCountryCode,
  isEmptyData,
  useDataWithMetric,
} from 'utils'
import CountryTick from 'components/common/CountryTick/CountryTick'
import BarChartComponent from 'components/common/BarChartCountry/BarChartComponent'
import { BarChartGlobalData, DownloadType } from 'types'
import { useActions, useAppSelector } from 'hooks'
import Tooltip from './components/CustomToolTip'
import DownloadImages from 'components/common/Download/DownloadImages'
import { useImageDownload } from 'components/common/Download/useImageDownload'
import { DATA_FILE_PATH, DOWNLOAD_FILE_NAME, METRIC_ENDPOINT_RANK, METRIC_ENDPOINT_WEIGHT } from 'constant'
import ChartHeader from 'components/common/ChartHeader/ChartHeader'

// const renderTick = (dataParam: BarChartGlobalData) => {
//   const maxScore = Math.ceil(Math.max(...dataParam.map((entry) => entry.score)))
//   return Array.from({ length: Math.ceil(maxScore / 5) + 1 }, (_, index) => index * 5)
// }
const renderTick = (dataParam: BarChartGlobalData) =>
  scaleLinear()
    .domain([0, _.max(dataParam.map((el) => el.score)) ?? 0])
    .ticks()
    .filter((el) => el % 10 === 0)

export default function Chart() {
  const configuration = useAppSelector(({ vibrancy }) => vibrancy.configuration)
  const countryData = useAppSelector(({ vibrancy }) => vibrancy.countryData)
  const metricGroups = useAppSelector(({ vibrancy }) => vibrancy.metricGroup)
  const renderChartTimes = useAppSelector(({ vibrancy }) => vibrancy.renderChartTimes)
  const selectedMetricValue = useAppSelector(({ vibrancy }) => vibrancy.configuration.metricValue)
  const currentYear = useAppSelector(({ vibrancy }) => vibrancy.configuration.yearFilter.currentYear)
  const { setBarChartGlobalData, setRanking, setCountry, setCountryType, setShowDwnLoader, setComparingCountries } =
    useActions()

  // const newFullData = useDataWithMetric(`${DATA_FILE_PATH}/full_data_06.27.24.csv`)
  // const newVariableList = useDataWithMetric(`${DATA_FILE_PATH}variable_list_06.27.24.csv`)

  // const processed = useMemo(() => {
  //   const metricBaseList = newVariableList?.filter(
  //     (m: any) => m?.display_name && m?.display_data_source && m?.pillar && m?.pillar_color_code,
  //   )
  //   const firstRecord = newFullData?.[0]
  //   const codeBookData: any[] = []

  //   let idIterator = 1
  //   metricBaseList?.forEach((m: any) => {
  //     const columnName = m.column_name
  //     const normColumn = `norm_${columnName}`
  //     const pcColumn = `pc_${columnName}`
  //     const normPcColumn = `norm_pc_${columnName}`

  //     if (firstRecord?.hasOwnProperty(columnName)) {
  //       codeBookData.push({
  //         id: idIterator++,
  //         pillar_1: m.pillar,
  //         pillar_2: '',
  //         shortname_raw: columnName,
  //         shortname_scaled: normColumn,
  //         metric_name: m.display_name,
  //         Definition: m.display_description,
  //         source: m.display_data_source,
  //       })
  //     }
  //     if (firstRecord?.hasOwnProperty(pcColumn)) {
  //       codeBookData.push({
  //         id: idIterator++,
  //         pillar_1: m.pillar,
  //         pillar_2: '',
  //         shortname_raw: pcColumn,
  //         shortname_scaled: normPcColumn,
  //         metric_name: `${m.display_name} PC`,
  //         Definition: m.display_description,
  //         source: m.display_data_source,
  //       })
  //     }
  //   })

  //   return codeBookData
  // }, [newFullData, newVariableList])

  const renderCustomAxisTick = ({ payload, y }: { payload: { value: string }; y: number }) => {
    return (
      <CountryTick
        countryCode={payload.value as keyof typeof Flags}
        id={data.find((el) => el.name === payload.value)?.id ?? 0}
        countryName={data.find((el) => el.name === payload.value)?.countryName ?? ''}
        position={{ y }}
        onClick={() => {
          setRanking({ ranking: 'national' })
          setCountryType({ countryType: 'national' })
          setCountry({ country: getCountryLongCodeFromCountryCode(countryData, payload.value) as string })
        }}
      />
    )
  }

  const renderChartData = calculateWeightedGlobalBarChart(metricGroups, selectedMetricValue, countryData, currentYear)
  // console.log('___render_ChartData_Pre-Render:', renderChartData)

  const data = useMemo<BarChartGlobalData>(() => {
    const { yearFilter, metricValue } = configuration
    if (metricGroups.length === 0 || countryData.length === 0 || yearFilter.yearRange.length === 0) return []

    const dataReturn = generateGlobalBarChartData(metricGroups, renderChartData, {
      globalYear: yearFilter.currentYear,
      metricEndpointWeight: METRIC_ENDPOINT_WEIGHT,
      metricEndpointRank: METRIC_ENDPOINT_RANK,
      metricValue: metricValue,
    })
    return dataReturn
  }, [countryData.length, metricGroups.length, renderChartTimes])

  // console.log('___render_BarChart:', data)
  // console.log('___data_metricGroups:', metricGroups)

  useEffect(() => {
    setBarChartGlobalData(data)
  }, [data])

  // Download images
  const fileNameDownload = DOWNLOAD_FILE_NAME.globalAiVibrancyRankingBar
  const [clickDownload, setClickDownload] = useState<DownloadType>(null)
  const { handleCallDownload, divRef } = useImageDownload()
  const handleDivDownload = (type: DownloadType) => {
    setShowDwnLoader(true)
    setClickDownload(type)
  }
  useEffect(() => {
    if (clickDownload) {
      handleCallDownload(clickDownload, { fileName: fileNameDownload, orientation: 'portrait', isSquareDownload: true })
      setClickDownload(null)
    }
  }, [clickDownload])

  const yearSource = new Date().getFullYear()

  useEffect(() => {
    if (data) {
      const top4Countries = data.slice(0, 4).map((eachData) => {
        return getCountryLongCodeFromCountryCode(countryData, eachData.name) as string
      })
      setComparingCountries({ countries: top4Countries })
    }
  }, [data])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', position: 'relative' }}>
      <DownloadImages
        handleDownPNG={() => handleDivDownload('png')}
        handleDownJPG={() => handleDivDownload('jpg')}
        handleDownPDF={() => handleDivDownload('pdf')}
      />
      <Box sx={{ padding: '24px 24px 0px 24px' }}>
        <ChartHeader
          header1={`${configuration.yearFilter.currentYear} Global AI Vibrancy Ranking`}
          header2={`Weighted Index Score | Source: ${yearSource} AI Index`}
        />
        <br />
        {!isEmptyData(data) && (
          <BarChartComponent
            data={data}
            isStackedBarChart
            xAxisTicks={renderTick(data)}
            yAxisTickRenderFunc={renderCustomAxisTick}
            tooltipContent={`Click on an individual country to visit that country's profile`}
            CustomToolTipComponent={Tooltip}
            onClickBarChartEvent={(eleOnClick) => {
              setRanking({ ranking: 'national' })
              setCountryType({ countryType: 'national' })
              setCountry({ country: getCountryLongCodeFromCountryCode(countryData, eleOnClick.name) as string })
            }}
            configuration={{ currentGrouping: configuration.grouping.currentGrouping }}
          />
        )}
      </Box>
      <Box sx={{ zIndex: -1, position: 'absolute', padding: '24px 24px 0px 24px' }} ref={divRef}>
        <ChartHeader
          header1={`${configuration.yearFilter.currentYear} Global AI Vibrancy Ranking`}
          header2={`Weighted Index Score | Source: ${yearSource} AI Index`}
        />
        <br />
        {!isEmptyData(data) && (
          <BarChartComponent
            data={data}
            isStackedBarChart
            xAxisTicks={renderTick(data)}
            yAxisTickRenderFunc={renderCustomAxisTick}
            tooltipContent={`Click on an individual country to visit that country's profile`}
            CustomToolTipComponent={Tooltip}
            onClickBarChartEvent={(eleOnClick) => {
              setRanking({ ranking: 'national' })
              setCountryType({ countryType: 'national' })
              setCountry({ country: getCountryLongCodeFromCountryCode(countryData, eleOnClick.name) as string })
            }}
            configuration={{ currentGrouping: configuration.grouping.currentGrouping }}
            is4Downloaded
          />
        )}
      </Box>
    </Box>
  )
}
